import { apiClient } from "@/api/api-client";
import { truncateName } from "@/api/helper";
import SetBranchModal from "@/components/cases/set-branch-modal";
import Badge from "@/components/core/badge";
import { Button } from "@/components/core/buttons/button";
import DataTable, { type ColumnDef } from "@/components/core/data-table";
import { useModal } from "@/components/core/modals/use-modal";
import Pagination from "@/components/core/pagination";
import { Searchbar } from "@/components/core/searchbar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { caseActivityPaymentQueryOptions } from "@/features/case-record/api/queries/case-activitiy-payment-query";
import { caseRecordsQueryOptions } from "@/features/case-record/api/queries/case-records-query";
import type { CaseRecord } from "@/features/case-record/types/case-records";
import { formatDate } from "@/utils/format-date";
import { DropdownMenuItem } from "@radix-ui/react-dropdown-menu";
import { IconPlus, IconTerminal2 } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";

export const Route = createFileRoute("/_backoffice/case-classification")({
	validateSearch: z.object({
		status: z.string().optional().default("occ_pending"),
		search: z.string().optional(),
		stage: z.string().optional(),
		per_page: z.number().optional(),
		page: z.number().nullable().optional(),
	}),
	loaderDeps: ({ search }) => ({
		page: search.page,
		per_page: search.per_page,
		status: search.status,
		stage: search.stage,
		search: search.search,
	}),
	loader: ({ context: { queryClient }, deps }) => {
		queryClient.ensureQueryData(
			caseActivityPaymentQueryOptions({
				...deps,
			}),
		);

		return queryClient.ensureQueryData(
			caseRecordsQueryOptions({
				...deps,
			}),
		);
	},
	component: RouteComponent,
});

function RouteComponent() {
	const [totalCases, setTotalCases] = useState(0);
	const [totalMotions, setTotalMotions] = useState(0);
	const search = Route.useSearch();

	const navigate = useNavigate({
		from: "/case-classification",
	});
	const { tabs, ...filteredSearch } = search;
	const casesQuery = useSuspenseQuery(
		caseRecordsQueryOptions({
			...filteredSearch,
		}),
	);

	const fetchCaseCount = async () => {
		const response = await apiClient.get("/backoffice/case-counts");
		setTotalCases(response.data.data?.total_cases);
		setTotalMotions(response.data.data?.total_motions);
	};

	useEffect(() => {
		fetchCaseCount();
	}, []);

	const meta = casesQuery.data.meta;
	const cases = casesQuery.data?.data;

	const motionQuery = useSuspenseQuery(
		caseActivityPaymentQueryOptions({
			...filteredSearch,
		}),
	);
	const metaMotion = motionQuery.data.meta;
	const motions = motionQuery.data?.data;
	const modal = useModal();

	const motionColumns: ColumnDef<CaseRecord>[] = [
		{
			id: "case_number",
			label: "Case No.",
			cellClassName: "min-w-[300px]",
			cell: ({ data }) => (
				<Link to={`/case-payments/${data.id}?id=${data.activity_id}`} className="font-semibold text-blue-600 hover:text-blue-500">
					{truncateName(data.case_number, 35)}
				</Link>
			),
		},
		{
			id: "activity",
			label: "Motions & Pleadings",
			cellClassName: "min-w-[200px]",
			cell: ({ data }) => data.activity,
		},
		{
			id: "court_office",
			label: "Court Station/Office",
			cellClassName: "min-w-[200px]",
			cell: ({ data }) => data.station,
		},
		{
			id: "created_at",
			label: "Date Filed",
			cellClassName: "min-w-[200px]",
			cell: ({ data }) => data.created_at,
		},
	];

	const columns: ColumnDef<CaseRecord>[] = [
		{
			id: "case_title",
			label: "Case Title",
			cellClassName: "min-w-[300px]",
			cell: ({ data, onClick }) => (
				<Link to={`/case-classification/${data.id}?status=${data.status}`} className="font-semibold text-blue-600 hover:text-blue-500">
					{truncateName(data.case_title, 35)}
				</Link>
			),
		},
		{
			id: "court_office",
			label: "Court Station/Office",
			cellClassName: "min-w-[200px]",
			cell: ({ data }) => data.station,
		},
		{
			id: "case_category",
			label: "Case Category",
			cellClassName: "",
			cell: ({ data }) => <span>{data.case_category}</span>,
		},
		{
			id: "case_type",
			label: "Case Type",
			cellClassName: "",
			cell: ({ data }) => <span>{data.case_type}</span>,
		},
		{
			id: "filed_at",
			label: "Date Filed",
			cellClassName: "min-w-[200px]",
			cell: ({ data }) => formatDate(data.filed_at, "PPp"),
		},
	];

	const branchColumns: ColumnDef<CaseRecord>[] = [
		{
			id: "case_number",
			label: "Case No.",
			cellClassName: "min-w-[200px]",
			cell: ({ data }) => data.case_number,
		},
		{
			id: "case_title",
			label: "Case Title",
			cellClassName: "min-w-[300px]",
			cell: ({ data, onClick }) => (
				<Link to={`/case-classification/${data.id}?status=${data.status}`} className="font-semibold text-blue-600 hover:text-blue-500">
					{truncateName(data.case_title, 35)}
				</Link>
			),
		},
		{
			id: "court_office",
			label: "Court Station/Office",
			cellClassName: "min-w-[200px]",
			cell: ({ data }) => data.station,
		},
		{
			id: "branch",
			label: "Branch",
			cellClassName: "min-w-[200px]",
			cell: ({ data }) => data.branch,
		},
		{
			id: "filed_at",
			label: "Date Filed",
			cellClassName: "min-w-[200px]",
			cell: ({ data }) => formatDate(data.filed_at, "PPp"),
		},
	];

	return (
		<Content>
			<Header
				title="Cases"
				action={
					<Link to="/case-classification/create?user=occ">
						<Button size="sm">
							<IconPlus size={18} stroke={2} />
							<span className="">Add New</span>
						</Button>
					</Link>
				}
			/>
			<div className="flex flex-col w-full max-w-base p-6">
				<div className="flex flex-col w-full gap-3">
					<Searchbar />
					<Tabs
						value={search.tabs || "cases"}
						defaultValue={search.tabs || "cases"}
						onValueChange={(value) => {
							navigate({
								to: "/case-classification",
								search: (old) => ({
									...old,
									tabs: value,
								}),
								replace: true,
							});
						}}
					>
						<TabsList className="justify-center">
							<TabsTrigger value="cases" count={totalCases ? totalCases : null}>
								Filed Cases
							</TabsTrigger>
							<TabsTrigger value="motions" count={totalMotions ? totalMotions : null}>
								Motions And Pleadings
							</TabsTrigger>
						</TabsList>
						<TabsContent value="cases">
							<Tabs
								value={search.status || "occ_pending"}
								defaultValue={search.status || "occ_pending"}
								onValueChange={(value) => {
									navigate({
										to: "/case-classification",
										search: (old) => ({
											...old,
											status: value,
										}),
										replace: true,
									});
								}}
							>
								<TabsList>
									<TabsTrigger value="occ_pending" count={meta.count_per_status?.occ_pending}>
										Pending Assessment
									</TabsTrigger>
									<TabsTrigger value="for_raffling" count={meta.count_per_status?.for_raffling}>
										For Raffling
									</TabsTrigger>
									<TabsTrigger value="branch_receiving" count={meta.count_per_status?.branch_receiving}>
										Branch Receiving
									</TabsTrigger>
									<TabsTrigger value="received" count={meta.count_per_status?.received}>
										Received
									</TabsTrigger>
									<TabsTrigger value="deferred_raffling" count={meta.count_per_status?.deferred_raffling}>
										Deferred
									</TabsTrigger>
									<TabsTrigger value="inhibit" count={meta.count_per_status?.inhibit}>
										Inhibit
									</TabsTrigger>
								</TabsList>
								<TabsContent value="occ_pending">
									<div className="mt-5">
										<DataTable
											className="border-collapse"
											data={cases}
											columns={columns}
											stickyHead={false}
											stickyCol={false}
											maxHeight={480}
											showBorder={false}
											compact={false}
											options={(row) => (
												<>
													<DropdownMenuItem
														onClick={() => {
															const branchModal = modal.openModal({
																title: "Assign a Branch",
																size: "lg",
																component: <SetBranchModal data={row} />,
															});
														}}
													>
														<div className="flex flex-row items-center w-full py-1 px-2 gap-2">
															<IconTerminal2 size={16} stroke={2} />
															<span>Assign a Branch</span>
														</div>
													</DropdownMenuItem>
												</>
											)}
										/>

										<Pagination
											onClick={async (page) => {
												await navigate({
													to: "/case-classification",
													search: (old) => ({
														...old,
														page,
													}),
												});
											}}
											initialPage={meta.current_page}
											pageCount={meta.last_page}
										/>
									</div>
								</TabsContent>

								<TabsContent value="for_raffling">
									<div className="mt-5">
										<DataTable
											className="border-collapse"
											data={cases}
											columns={columns}
											stickyHead={false}
											stickyCol={false}
											maxHeight={480}
											showBorder={false}
											compact={false}
										/>

										<Pagination
											onClick={async (page) => {
												await navigate({
													to: "/case-classification",
													search: (old) => ({
														...old,
														page,
													}),
												});
											}}
											initialPage={meta.current_page}
											pageCount={meta.last_page}
										/>
									</div>
								</TabsContent>

								<TabsContent value="branch_receiving">
									<div className="mt-5">
										<DataTable
											className="border-collapse"
											data={cases}
											columns={branchColumns}
											stickyHead={false}
											stickyCol={false}
											maxHeight={480}
											showBorder={false}
											compact={false}
										/>

										<Pagination
											onClick={async (page) => {
												await navigate({
													to: "/case-classification",
													search: (old) => ({
														...old,
														page,
													}),
												});
											}}
											initialPage={meta.current_page}
											pageCount={meta.last_page}
										/>
									</div>
								</TabsContent>

								<TabsContent value="received">
									<div className="mt-5">
										<DataTable
											className="border-collapse"
											data={cases}
											columns={branchColumns}
											stickyHead={false}
											stickyCol={false}
											maxHeight={480}
											showBorder={false}
											compact={false}
										/>

										<Pagination
											onClick={async (page) => {
												await navigate({
													to: "/case-classification",
													search: (old) => ({
														...old,
														page,
													}),
												});
											}}
											initialPage={meta.current_page}
											pageCount={meta.last_page}
										/>
									</div>
								</TabsContent>

								<TabsContent value="deferred_raffling">
									<div className="mt-5">
										<DataTable
											className="border-collapse"
											data={cases}
											columns={branchColumns}
											stickyHead={false}
											stickyCol={false}
											maxHeight={480}
											showBorder={false}
											compact={false}
										/>

										<Pagination
											onClick={async (page) => {
												await navigate({
													to: "/case-classification",
													search: (old) => ({
														...old,
														page,
													}),
												});
											}}
											initialPage={meta.current_page}
											pageCount={meta.last_page}
										/>
									</div>
								</TabsContent>

								<TabsContent value="inhibit">
									<div className="mt-5">
										<DataTable
											className="border-collapse"
											data={cases}
											columns={branchColumns}
											stickyHead={false}
											stickyCol={false}
											maxHeight={480}
											showBorder={false}
											compact={false}
										/>

										<Pagination
											onClick={async (page) => {
												await navigate({
													to: "/case-classification",
													search: (old) => ({
														...old,
														page,
													}),
												});
											}}
											initialPage={meta.current_page}
											pageCount={meta.last_page}
										/>
									</div>
								</TabsContent>
							</Tabs>
						</TabsContent>
						<TabsContent value="motions">
							<Tabs
								defaultValue={search.status || "occ_pending"}
								onValueChange={async (value) => {
									await navigate({
										to: "/case-classification",
										search: (old) => ({
											...old,
											status: value,
										}),
									});
								}}
							>
								<TabsList className="justify-center">
									<TabsTrigger value="occ_pending" count={metaMotion.count_per_status?.[""]}>
										Pending
									</TabsTrigger>
									<TabsTrigger value="confirmed" count={metaMotion.count_per_status?.confirmed}>
										Confirmed
									</TabsTrigger>
								</TabsList>

								<DataTable
									className="border-collapse"
									data={motions}
									columns={motionColumns}
									stickyHead={false}
									stickyCol={false}
									maxHeight={480}
									showBorder={false}
									compact={false}
								/>
								<Pagination
									onClick={async (page) => {
										await navigate({
											to: "/case-classification",
											search: (old) => ({
												...old,
												page,
											}),
										});
									}}
									initialPage={metaMotion.current_page}
									pageCount={metaMotion.last_page}
								/>
							</Tabs>
						</TabsContent>
					</Tabs>
				</div>
			</div>
		</Content>
	);
}
